import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import MagicBall from '../../assets/images/magic-ball.svg';
import Unbiased from '../../assets/images/unbiased.svg';
import Star from '../../assets/images/start.svg';
import LightBulb from '../../assets/images/light-bulb.svg';
import Accuracy from '../../assets/images/accuracy.svg';
import Shield from '../../assets/images/shield.svg';
import Startup from '../../assets/images/startup.svg';
import { YellowBoxBottom } from './Home';

const StyledPaper = styled(Paper)(({ theme }) => ({
  paddingLeft: '3rem',
  paddingRight: '3rem',
  paddingTop: '36px',
  backgroundColor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f7f7f7',
  color: '#909090',
  height: '43px',
}));

const FeatureSection = ({
  title,
  subtitle,
  icon,
  heading,
  description,
  bulletPoints,
  bigIcon,
  boldText,
  sx,
}) => {
  const iconSize = bigIcon ? 60 : 48;
  const textWeight = boldText ? '600' : '500';
  return (
    <Box sx={{ marginTop: '80px', ...sx }}>
      <Typography variant="h3" fontWeight="600">
        {title}
        {subtitle && ': '}
        {subtitle}
      </Typography>

      <Card elevation={0} sx={{ bgcolor: 'transparent' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              component="img"
              src={icon}
              alt={heading}
              sx={{ width: iconSize, height: iconSize }}
            />

            <Stack spacing={1}>
              {description && description.length > 0 && (
                <Typography variant="homePageBody" fontWeight={textWeight}>
                  {description}
                </Typography>
              )}
              <List dense disablePadding sx={{ pl: 1 }}>
                {bulletPoints.map((point, index) => (
                  <ListItem
                    disablePadding
                    key={index}
                    disableGutters
                    sx={{ display: 'list-item', pl: 0 }}
                  >
                    <ListItemText
                      primary={`• ${point}`}
                      sx={{ '.MuiTypography-root': { fontWeight: textWeight } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const WeAreDifferent = () => {
  return (
    <Box>
      <Typography variant="homePageBody" paragraph>
        <span style={{ fontFamily: 'SpaceGrotesk' }}>KaliyugAi</span> isn’t just
        another astrology chatbot draped in spiritual lingo it’s a
        high-precision AI system, trained from the ground up on verified Vedic
        scriptures and backed by measurable accuracy. Where most platforms rely
        on generic, long-winded AI outputs or inconsistent astrologer panels, we
        offer something fundamentally different: an intelligent, data-driven
        system designed to deliver clarity, not confusion.
      </Typography>
      <Typography variant="homePageBody" paragraph>
        While others plug prompts into third-party LLMs (like ChatGPT) and call
        it "astrology," we built our system from scratch encoding centuries-old
        Vedic principles into structured models that understand, analyze, and
        respond like a true astrologer, not a black box autocomplete agent.
        Every insight is based on real logic, not recycled paragraphs. Every
        answer is designed to help you move forward, not scare you into buying
        more.
      </Typography>

      <StyledPaper
        variant="outlined"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="homePageBody" fontWeight={500} align="center">
          We're not in the business of emotional manipulation. We're here to
          empower.
        </Typography>
      </StyledPaper>
      <Divider />
      <Typography variant="h2">
        Accurate Unbiased and Always Available
      </Typography>

      <FeatureSection
        title="Accurate"
        subtitle="Precision You Can Trust"
        icon={MagicBall}
        heading="Accurate"
        description="Our AI is trained on vast datasets rooted in traditional astrology and fine-tuned with modern algorithms. With an 80%+ accuracy rate, we deliver predictions you can rely on whether it’s about love, career, finance, or personal growth."
        bulletPoints={[
          'Each question is analyzed using your birth chart and planetary positions.',
          'No generic answers, everything is personalized to you.',
          'Tested with thousands of real-world cases to ensure high-quality output.',
        ]}
        bigIcon={true}
      />
      <YellowBoxBottom sx={{ textAlign: 'left' }}>
        <FeatureSection
          title="80%+ Prediction Accuracy"
          subtitle=""
          icon={Accuracy}
          heading=""
          description=""
          bulletPoints={[
            'We’ve fine-tuned our AI system to deliver an exceptionally high accuracy rate of over 80% far beyond what traditional chat-based astrology platforms can offer ',
            'Every answer is rooted in precise planetary alignment, using your birth details to generate highly relevant insights.',
            'No guesswork. No generic copy-paste replies.',
            'You get fast, reliable, and deeply personalised guidance every single time.',
          ]}
          sx={{ textAlign: 'left', marginTop: '36px' }}
          boldText={true}
        />
      </YellowBoxBottom>
      <FeatureSection
        title="Unbiased"
        subtitle="The Truth Without Judgment"
        icon={Unbiased}
        heading="Unbiased"
        description="Unlike human astrologers who may interpret things differently, our AI system remains consistent and neutral."
        bulletPoints={[
          'Every answer is based purely on astrological logic not emotions, personal opinions, or assumptions.',
          'You and every other user get the same accurate response to the same question under the same conditions.',
          'Our platform ensures fairness, transparency, and repeatability in every prediction.',
        ]}
        bigIcon={true}
      />
      <FeatureSection
        title="Always Available"
        subtitle="Astrology, Anytime You Need It"
        icon={Star}
        heading="Always Available"
        description="No need to wait for appointments or worry about time zones. Our AI astrologer is here for you 24/7, ready to answer your questions instantly."
        bulletPoints={[
          'Whether it’s 3 AM or a lunch break, get instant guidance on your terms.',
          'Ask as much or as little as you want our token system gives you full control.',
          'Your spiritual guide is just a tap away whenever clarity calls.',
        ]}
        bigIcon={true}
      />
      <Divider />
      <FeatureSection
        title="Powered by AI, Rooted in Astrology"
        subtitle=""
        icon={LightBulb}
        heading=""
        description="Our AI engine is trained on centuries of astrological knowledge, blending ancient systems with modern data interpretation. This means:"
        bulletPoints={[
          'You get reliable predictions tailored to your birth details.',
          'No fluff, just meaningful, data-backed guidance.',
          'Works instantly, no need to wait for an astrologer.',
        ]}
      />
      <FeatureSection
        title="Secure & Confidential"
        subtitle=""
        icon={Shield}
        heading=""
        description="We respect your privacy deeply."
        bulletPoints={[
          'Your data is encrypted and stored securely.',
          'No data is ever shared with third parties.',
          'You’re in control of your questions and insights.',
        ]}
      />
      <FeatureSection
        title="Why We’re Revolutionary"
        subtitle=""
        icon={Startup}
        heading=""
        description=""
        bulletPoints={[
          '24/7 Instant Access – No need to book appointments.',
          'Token-based Model – Ask only what you need, no monthly fees.',
          'Always Evolving – Our system learns and improves over time.',
          'Astrology for All – From beginners to pros, we make astrology accessible, accurate, and personal.',
        ]}
      />

      <Divider />
      <YellowBoxBottom sx={{ padding: '3rem' }}>
        <Typography variant="homePageBody" fontWeight={600} align="center">
          ✨ We’re here to change how the world experiences astrology one
          accurate answer at a time.
          <br /> Start your journey with insights that are meaningful,
          consistent and made just for you.
        </Typography>
      </YellowBoxBottom>
    </Box>
  );
};

export default WeAreDifferent;
